import type { GalleryMediaFile } from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { MediaFilePreview as MediaFilePreviewView } from "@next-platform/client-common-spicy-ui";
import { memo } from "react";

import useMediaFileActionCapabilities from "../MediaFileDetails/useMediaFileActionCapabilities";
import useMediaFileDownload from "../MediaFileDetails/useMediaFileDownload";
import type { MediaFilePreviewProps } from "./types";
import useCopyMediaFilePreviewLink from "./useCopyMediaFilePreviewLink";
import useMediaFilePreviewContent from "./useMediaFilePreviewContent";
import useMediaFilePreviewNavigation from "./useMediaFilePreviewNavigation";

type MediaFilePreviewContentProps = Omit<MediaFilePreviewProps, "file"> & { readonly file: GalleryMediaFile };

const MediaFilePreviewContent = ({ file, isBlurred, "data-testid": testId = "MediaFilePreview", onOpen }: MediaFilePreviewContentProps) => {
  const { content, labels, metadata, linkTarget, handleOpen } = useMediaFilePreviewContent({ file, isBlurred, onOpen });
  const { pageHref, relatedEntityHref, handleOpenRelatedEntity } = useMediaFilePreviewNavigation({ id: metadata.id, linkTarget });
  const handleCopyLink = useCopyMediaFilePreviewLink(pageHref);
  const isDownloadAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.MediaDownload);
  // The five other gated rows (Keep, Make private, Add to folder, Report, Delete) read the same hook here and
  // in the details view, so a brand's menu is identical wherever it is opened from. Copy link is never gated.
  const actionCapabilities = useMediaFileActionCapabilities();
  // Same hook the details view uses; its file union is exactly GalleryMediaFile, so nothing tile-specific was
  // needed. The failure fallback goes through with the handler, so a download that could not start still
  // leaves a real "Open original" link in the tile's menu, as it does in the details view.
  const { onDownload, fallbackUrl } = useMediaFileDownload({ id: metadata.id, file });

  return (
    <MediaFilePreviewView
      data-testid={testId}
      content={content}
      labels={labels}
      characterHref={relatedEntityHref}
      onOpen={handleOpen}
      onOpenCharacter={handleOpenRelatedEntity}
      onCopyLink={handleCopyLink}
      onDownload={isDownloadAllowed ? onDownload : undefined}
      downloadFallbackUrl={isDownloadAllowed ? fallbackUrl : undefined}
      actionsAllowed={actionCapabilities}
    />
  );
};

export default memo(MediaFilePreviewContent);
